import { useMutation, useQuery } from 'react-query';

import { ErrorResponse } from 'api/types/errors';
import {
  getOrganizationMembership,
  getOrganizationMemberships,
  updateOrganizationMembership,
  createOrganizationMembership,
  exportOrganizationMembershipsCSV
} from 'api/services/organizations/memberships';
import {
  IncomingOrganizationMembershipProductUpdateDto,
  IncomingOrganizationMembershipProductCreateDto,
  OutgoingMembershipProductDto
} from 'api/dtos/memberships/products';

export function useGetOrganizationMemberships({
  organizationId,
  enabled = true
}: {
  organizationId: string;
  enabled?: boolean;
}) {
  return useQuery<OutgoingMembershipProductDto[], ErrorResponse>(
    ['organizationMemberships', { organizationId }],
    () => getOrganizationMemberships(organizationId),
    {
      enabled
    }
  );
}

export function useGetOrganizationMembership({
  organizationId,
  membershipTierId,
  enabled = true
}: {
  organizationId: string;
  membershipTierId: string;
  enabled?: boolean;
}) {
  return useQuery<OutgoingMembershipProductDto, ErrorResponse>(
    ['organizationMembership', { organizationId, membershipTierId }],
    () => getOrganizationMembership(organizationId, membershipTierId),
    {
      enabled
    }
  );
}

export function useUpdateOrganizationMembership() {
  return useMutation<
    OutgoingMembershipProductDto,
    ErrorResponse,
    {
      organizationId: string;
      membershipTierId: string;
      membershipTier: IncomingOrganizationMembershipProductUpdateDto;
    }
  >(updateOrganizationMembership);
}

export function useCreateOrganizationMembership() {
  return useMutation<
    OutgoingMembershipProductDto,
    ErrorResponse,
    {
      organizationId: string;
      membershipTier: IncomingOrganizationMembershipProductCreateDto;
    }
  >(createOrganizationMembership);
}

export function useExportOrganizationMembershipsCSV({
  organizationId,
  enabled = false
}: {
  organizationId: string;
  enabled?: boolean;
}) {
  return useQuery<Blob, ErrorResponse>(
    ['exportOrganizationMembershipsCSV', { organizationId }],
    () => exportOrganizationMembershipsCSV(organizationId),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false
    }
  );
}
